import merge from 'lodash/merge';

import { createDashHttpClient } from '@/apis/dash.http';
import { env } from '@/env';
import { parseQuery } from '@/utils/query';

export const http = createDashHttpClient(env.libraryApiUrl, {
  camelizeKeys: true,
});
// instance for utm data, some keys and value comes from user input
export const utmHttp = createDashHttpClient(env.libraryApiUrl, {
  camelizeKeys: false,
  decamelizeRequests: false,
});

export function getMediaV2({ brandId, mediaId }, config = {}) {
  return http.get(`/brands/${brandId}/media/v2/${mediaId}`, config);
}

/**
 * @param {Object} params
 * @param {number} params.brandId
 * @returns {Promise<import('@/types/http').HttpResponse<{ canAccessVideoGeneration: boolean, limit: number, used: number, expiresAt: string }>>}
 */
export async function getVideoGenerationUsage({ brandId }, config = {}) {
  return http.get(`/brands/${brandId}/video_generation/usage`, config);
}

export function getGalleriesCSV({ brandId, ...params }, config = {}) {
  return http.get(
    `/brands/${brandId}/galleries/csv`,
    merge(
      {
        params,
      },
      config,
    ),
  );
}

export function getStoryMetricsCSV({ brandId, ...params }, config = {}) {
  return http.post(
    `/brands/${brandId}/galleries/timeseries_metrics/csv`,
    {},
    merge(
      {
        params,
      },
      config,
    ),
  );
}

export function getBoardsMediaCSV({ brandId, ...params }, config = {}) {
  return http.get(
    `/brands/${brandId}/galleries/media/csv`,
    merge(
      {
        params,
      },
      config,
    ),
  );
}

export function getBoardMediaCSV({ brandId, galleryId, ...params }, config = {}) {
  return http.get(
    `/brands/${brandId}/galleries/${galleryId}/media/csv`,
    merge(
      {
        params,
      },
      config,
    ),
  );
}

export function getGalleryMediaProductsCSV({ brandId, galleryId, ...params }, config = {}) {
  return http.get(
    `/brands/${brandId}/galleries/${galleryId}/gallery_media_products/csv`,
    merge(
      {
        params,
      },
      config,
    ),
  );
}

export function getGalleryStatsCSV({ brandId, galleryId, ...params }, config = {}) {
  return http.get(
    `/brands/${brandId}/galleries/${galleryId}/stats/csv`,
    merge(
      {
        params,
      },
      config,
    ),
  );
}

export function getGalleryUgcAsAltImageCSV({ brandId, galleryId, ...params }, config = {}) {
  return http.get(
    `/brands/${brandId}/galleries/${galleryId}/ugc_as_alt_image/csv`,
    merge(
      {
        params,
      },
      config,
    ),
  );
}

export function getLikeShopEmailSubmissionsCSV({ brandId, ...params }, config = {}) {
  return http.get(
    `/brands/${brandId}/galleries/likeshop/email_submissions/csv`,
    merge(
      {
        params,
      },
      config,
    ),
  );
}

export function getTikTokLikeShopEmailSubmissionsCSV({ brandId, ...params }, config = {}) {
  return http.get(
    `/brands/${brandId}/galleries/tiktok_likeshop/email_submissions/csv`,
    merge(
      {
        params,
      },
      config,
    ),
  );
}

export function getGallery({ brandId, galleryId }, config = {}) {
  return http.get(`/brands/${brandId}/galleries/${galleryId}`, config);
}

export async function createGallery(
  { brandId, name, description, tags, galleryType, duplicate, duplicateFilter },
  config = {},
) {
  return http.post(
    `/brands/${brandId}/galleries`,
    { name, description, tags, galleryType, duplicate, duplicateFilter },
    config,
  );
}

export async function updateGallery(
  { brandId, galleryId, name, description, tags, spiritGalleryEnabled, settings },
  config = {},
) {
  return http.patch(
    `/brands/${brandId}/galleries/${galleryId}`,
    { name, description, tags, spirit_gallery_enabled: spiritGalleryEnabled, settings },
    config,
  );
}

export async function deleteGallery({ brandId, galleryId }, config = {}) {
  return http.delete(`/brands/${brandId}/galleries/${galleryId}`, config);
}

export function getGalleries(
  {
    brandId,
    url,
    type,
    startDate,
    endDate,
    tags,
    tagsFilterType,
    search,
    sort,
    limit,
    summary,
    types,
    minSize,
    live,
    spiritGalleryEnabled,
    ids,
  },
  config = {},
) {
  const params = {
    type,
    startDate,
    endDate,
    tags,
    tagsFilterType,
    search,
    sort,
    limit,
    summary,
    types,
    minSize,
    live,
    spiritGalleryEnabled,
    ids,
  };
  return http.get(
    url ?? `/brands/${brandId}/galleries`,
    merge(
      {
        params: url ? null : params,
      },
      config,
    ),
  );
}

export function getFullGalleryTagList({ brandId, activeOnly, galleryType, sort }, config = {}) {
  const params = {
    activeOnly,
    galleryType,
  };
  if (sort) {
    params.sort = sort;
  }
  return http.get(
    `/brands/${brandId}/gallery_tags`,
    merge(
      {
        params,
      },
      config,
    ),
  );
}

export async function removeTagsFromGallery({ brandId, galleryId, tags }, config = {}) {
  return http.post(`/brands/${brandId}/galleries/${galleryId}/remove_tags`, { tags }, config);
}

export function getGalleryMedia({ brandId, galleryId, url, ...other }, config = {}) {
  const params = merge({}, other);
  if (url) {
    merge(params, parseQuery(url));
  }
  return http.get(
    `/brands/${brandId}/galleries/${galleryId}/media`,
    merge(
      {
        params,
      },
      config,
    ),
  );
}

export function addGalleryMedia({ brandId, galleryId, mediaIds }, config = {}) {
  return http.post(`/brands/${brandId}/galleries/${galleryId}/media`, { mediaIds }, config);
}

export function removeGalleryMediaBulk({ brandId, galleryId, mediaIds }, config = {}) {
  return http.delete(
    `/brands/${brandId}/galleries/${galleryId}/media`,

    merge(
      {
        params: {
          mediaIds: mediaIds?.join(',') ?? [],
        },
      },
      config,
    ),
  );
}

export function removeGalleryMedia({ brandId, galleryId, mediaId }, config = {}) {
  return http.delete(`/brands/${brandId}/galleries/${galleryId}/media/${mediaId}`, config);
}

export function getTimeSeriesMetricsInstagramStory({ brandId, scale, startDate, endDate }, config) {
  return http.get(
    `/brands/${brandId}/timeseries_metrics/instagram_story`,
    merge(
      {
        params: {
          scale,
          startDate,
          endDate,
        },
      },
      config,
    ),
  );
}

export async function deleteGalleryTag({ brandId, tagId }, config = {}) {
  return http.delete(`/brands/${brandId}/gallery_tags/${tagId}`, config);
}

export function getGalleryCSV({ brandId, galleryId, sort, socketId, type }, config = {}) {
  return http.post(
    `/brands/${brandId}/galleries/${galleryId}/csv`,
    { sort, socketId, type },
    config,
  );
}

export function getGalleryStats({ brandId, galleryId, ...params }, config = {}) {
  return http.get(
    `/brands/${brandId}/galleries/${galleryId}/stats`,
    merge(
      {
        params,
      },
      config,
    ),
  );
}

export function getLiveGalleriesStats({ brandId, ...params }, config = {}) {
  return http.get(
    `/brands/${brandId}/galleries/stats`,
    merge(
      {
        params,
      },
      config,
    ),
  );
}

export function updateCustomContentSettings({ brandId, galleryType, settings }, config = {}) {
  return http.patch(
    `/brands/${brandId}/galleries/${galleryType}/custom_content_settings`,
    settings,
    config,
  );
}

export function updateLikeshopCustomContentSettings(
  { brandId, bannerImage, pinnedContent, emailCapture },
  config = {},
) {
  // DEPRECATED: use updateCustomContentSettings
  return http.patch(
    `/brands/${brandId}/galleries/likeshop/custom_content_settings`,
    {
      bannerImage,
      pinnedContent,
      emailCapture,
    },
    config,
  );
}

export async function updateGalleryOrder({ brandId, galleryId, changeList }, config = {}) {
  return http.patch(`/brands/${brandId}/galleries/${galleryId}/order`, changeList, config);
}

export function getCompetitorTotalsByBrand({ brandId }, config = {}) {
  return utmHttp.get(`/brands/${brandId}/competitors/count`, config);
}

export async function getReport360LikeshopPosts({ brandId, month }, config = {}) {
  return http.get(`/brands/${brandId}/reports/360/likeshop`, merge({ params: { month } }, config));
}

export async function getTrendGroups({ brandId, source }, config = {}) {
  return http.get(
    `/brands/${brandId}/trend_groups`,
    merge(
      {
        params: { source },
      },
      config,
    ),
  );
}

export async function getTrendGroup({ brandId, trendGroupId }, config = {}) {
  return http.get(`/brands/${brandId}/trend_groups/${trendGroupId}`, config);
}

export async function createTrendGroup(
  { brandId, name, description, tags, targetSource },
  config = {},
) {
  return http.post(
    `/brands/${brandId}/trend_groups`,
    { name, description, tags, targetSource },
    config,
  );
}

export async function updateTrendGroup(
  { brandId, trendGroupId, name, description, tags },
  config = {},
) {
  return http.patch(
    `/brands/${brandId}/trend_groups/${trendGroupId}`,
    { name, description, tags },
    config,
  );
}

export async function deleteTrendGroup({ brandId, trendGroupId }, config = {}) {
  return http.delete(`/brands/${brandId}/trend_groups/${trendGroupId}`, config);
}

export async function addTrendGroupAccount({ brandId, trendGroupId, accountId }, config = {}) {
  return http.post(
    `/brands/${brandId}/trend_groups/${trendGroupId}/accounts`,
    { accountId },
    config,
  );
}

export async function removeTrendGroupAccount({ brandId, trendGroupId, accountId }, config = {}) {
  return http.delete(
    `/brands/${brandId}/trend_groups/${trendGroupId}/accounts/${accountId}`,
    config,
  );
}

export async function getTrendGroupStats(
  { brandId, trendId, period, resolution, sourceId },
  config = {},
) {
  return http.get(
    `/brands/${brandId}/trend_groups/${trendId}/stats`,
    merge({ params: { period, resolution, sourceId } }, config),
  );
}

export async function removeTrendGroupTag({ brandId, trendGroupId, tagId }, config = {}) {
  return http.delete(`/brands/${brandId}/trend_groups/${trendGroupId}/tags/${tagId}`, config);
}

export async function getTrendGroupTags({ brandId, activeOnly }, config = {}) {
  return http.get(
    `/brands/${brandId}/trend_group_tags`,
    merge(
      {
        params: {
          activeOnly,
        },
      },
      config,
    ),
  );
}

export async function getSegmentCluster(
  { brandId, segmentClusterId, isUgc, period, sort, resolution },
  config = {},
) {
  return http.get(
    `/brands/${brandId}/segment_clusters/${segmentClusterId}`,
    merge(
      {
        params: {
          isUgc,
          period,
          sort,
          resolution,
        },
        camelizeKeys: true,
      },
      config,
    ),
  );
}

export async function updateSegmentCluster({ brandId, segmentClusterId, tags }, config = {}) {
  return http.patch(`/brands/${brandId}/segment_clusters/${segmentClusterId}`, { tags }, config);
}

export async function removeSegmentClusterTag({ brandId, clusterId, tagId }, config = {}) {
  return http.delete(`/brands/${brandId}/segment_clusters/${clusterId}/tags/${tagId}`, config);
}

export async function deleteSegmentCluster({ brandId, clusterId }, config = {}) {
  return http.delete(`/brands/${brandId}/segment_clusters/${clusterId}`, config);
}

export async function getSegmentClusterTags({ brandId, activeOnly }, config = {}) {
  return http.get(
    `/brands/${brandId}/segment_cluster_tags`,
    merge(
      {
        params: { activeOnly },
      },
      config,
    ),
  );
}

export async function removeTagFromAllSegmentClusters({ brandId, tagId }, config = {}) {
  return http.delete(`/brands/${brandId}/segment_cluster_tags/${tagId}`, config);
}

export async function createSegmentClusterTag({ brandId, name, color }, config = {}) {
  return http.post(`/brands/${brandId}/segment_cluster_tags`, { name, color }, config);
}

export async function createSegmentClusterTagAssociations(
  { brandId, segmentClusterIds, segmentClusterTagIds },
  config = {},
) {
  await http.post(
    `/brands/${brandId}/segment_cluster_tag_associations`,
    { segmentClusterIds, segmentClusterTagIds },
    config,
  );
}

export async function getSegmentClusterStats(
  { brandId, clusterId, period, resolution, sourceId },
  config = {},
) {
  return http.get(
    `/brands/${brandId}/segment_clusters/${clusterId}/stats`,
    merge(
      {
        params: {
          period,
          resolution,
          sourceId,
        },
      },
      config,
    ),
  );
}

export async function getSegments(
  { brandId, source, sourceId, sort, period, resolution, mediaCount, isUgc, brand, token },
  config = {},
) {
  return http.get(
    `/brands/${brandId}/segments`,
    merge(
      {
        params: {
          source,
          sourceId,
          sort,
          period,
          resolution,
          mediaCount,
          isUgc,
          brand,
          token,
        },
      },
      config,
    ),
  );
}

export async function getSegment({ brandId, segmentId }, config = {}) {
  return http.get(`/brands/${brandId}/segments/${segmentId}`, config);
}

export async function getSegmentStats({ brandId, source, sourceId, isUgc }, config = {}) {
  return http.get(
    `/brands/${brandId}/segments/stats`,
    merge(
      {
        params: {
          source,
          sourceId,
          isUgc,
        },
      },
      config,
    ),
  );
}

export async function getContentTags({ organizationId, contentTagIds }, config = {}) {
  const params = {};
  if (contentTagIds?.length > 0) {
    params.ids = contentTagIds.join(',');
  }
  return http.get(`/organizations/${organizationId}/content_tags`, merge({ params }, config));
}

export async function createContentTag({ organizationId, name, brandIds }, config = {}) {
  const data = {
    name,
  };
  if (brandIds?.length > 0) {
    data.brandIds = brandIds;
  }
  return http.post(`/organizations/${organizationId}/content_tags`, data, config);
}

export async function updateContentTag(
  { organizationId, contentTagId, name, brandIds },
  config = {},
) {
  const data = {
    name,
    brandIds,
  };

  return http.patch(`/organizations/${organizationId}/content_tags/${contentTagId}`, data, config);
}

export async function deleteContentTag({ organizationId, contentTagId }, config = {}) {
  return http.delete(`/organizations/${organizationId}/content_tags/${contentTagId}`, config);
}

export async function deleteContentTagBrand(
  { organizationId, contentTagId, brandId },
  config = {},
) {
  return http.delete(
    `/organizations/${organizationId}/content_tags/${contentTagId}/brands/${brandId}`,
    config,
  );
}

export async function associateContentTagsToBrandMedia(
  { organizationId, contentTagIds, brandMediaIds, createMissingAssociations },
  config = {},
) {
  return http.post(
    `/organizations/${organizationId}/brand_media_content_tags`,
    {
      contentTagIds,
      brandMediaIds,
      createMissingAssociations,
    },
    config,
  );
}

export async function removeContentTagFromBrandMedia(
  { organizationId, contentTagId, brandMediaId },
  config = {},
) {
  return http.delete(
    `/organizations/${organizationId}/content_tags/${contentTagId}/brand_media/${brandMediaId}`,
    config,
  );
}

export async function getPredictions({ brandId, files }, config = {}) {
  return http.post(`/brands/${brandId}/media_predictions`, files, config);
}

export function getGalleriesForBrands({ search, types, brandIds, limit }, config = {}) {
  return http.post(
    '/brands/galleries/search',
    { search, types, brand_ids: brandIds, limit },
    config,
  );
}

export function packageBulkCroppedImages({ imageUrl, filename, crops }, config = {}) {
  return http.post('/image_export/bulk_crop', { imageUrl, filename, crops }, config);
}

const api = {
  async getGalleryMediaV2(
    {
      brandId,
      galleryId,
      offset = 0,
      limit = 100,
      sort,
      withProductsOnly = false,
      startDate,
      endDate,
      excludeInstagramOwnedWithoutProducts,
      include,
      url,
    },
    config = {},
  ) {
    const params = {
      offset,
      limit,
      withProductsOnly,
    };
    if (include) {
      params.include = include.join(',');
    }
    if (sort) {
      params.sort = sort;
    }
    if (startDate && endDate) {
      params.startDate = startDate;
      params.endDate = endDate;
    }
    if (excludeInstagramOwnedWithoutProducts) {
      params.excludeInstagramOwnedWithoutProducts = excludeInstagramOwnedWithoutProducts;
    }

    if (url) {
      merge(params, parseQuery(url));
    }

    return http.get(
      `/brands/${brandId}/galleries/${galleryId}/media/v2`,
      merge(
        {
          params,
        },
        config,
      ),
    );
  },
  async getInteractionMediaById({ brandId, mediaId }, config = {}, params = {}) {
    const url = `/brands/${brandId}/media/${mediaId}`;
    return http.get(url, merge({ params }, config));
  },
  async getMediaListV2(
    {
      brandId,
      limit,
      offset,
      sortFields,
      filters,
      url,
      includeCreator,
      campaignCreatorFiltersOptions,
    },
    config,
  ) {
    const params = { limit, offset };
    const data = { sortFields, filters, includeCreator, campaignCreatorFiltersOptions };

    if (url) {
      merge(params, parseQuery(url));
    }

    return http.put(`/brands/${brandId}/media/v2`, data, merge({ params }, config));
  },
  async getMediaSearch({ limit, offset, sortFields, filters, includeCreator }, config) {
    const data = { sortFields, filters, includeCreator, limit, offset };

    return http.post(`/media/search`, data, config);
  },
  async updateTikTokLikeShopCustomContent({ brandId, data }) {
    // DEPRECATED: use updateCustomContentSettings
    return http.patch(`/brands/${brandId}/galleries/tiktok_likeshop/custom_content_settings`, data);
  },
  async getUtmSettingsForTikTokLikeShop({ brandId, data }) {
    return utmHttp.get(`/brands/${brandId}/galleries/tiktok_likeshop`, data);
  },

  async saveUtmSettingsForTikTokLikeShop({ brandId, data, galleryId }) {
    return utmHttp.patch(`/brands/${brandId}/galleries/${galleryId}`, data);
  },
  async getGoogleAnalyticsAccounts({ brandId }) {
    return http.get(`/brands/${brandId}/google_analytics/accounts`);
  },
  async createAnalyticsAccount({ brandId, data }) {
    const path = `/brands/${brandId}/google_analytics/account`;
    return http.post(path, { ...data });
  },
  async getGoogleAnalyticAccountsByBrandId({ brandId }, payload) {
    return http.get(`/brands/${brandId}/google_analytics/list_accounts`, payload);
  },
  async getBrandsGoogleAnalyticsAccount({ brandId }) {
    return http.get(`/brands/${brandId}/google_analytics/account`);
  },
  async getSegmentClusters(
    {
      brandId,
      tagId,
      sources,
      isUgc,
      period,
      mediaCount,
      search,
      tags,
      tagsFilterType,
      sort,
      limit,
      offset,
      summary = true,
      url,
    },
    config = {},
  ) {
    const params = {
      tagId,
      sources,
      isUgc,
      period,
      mediaCount,
      search,
      tags,
      tagsFilterType,
      sort,
      limit,
      offset,
      summary,
    };
    Object.keys(params).forEach((key) => {
      if (params[key] === undefined) {
        delete params[key];
      }
    });
    return http.get(url || `/brands/${brandId}/segment_clusters`, {
      ...config,
      params,
    });
  },

  async getMediaSearchList({
    brandId,
    sort,
    query,
    aspectRatio,
    size,
    type,
    color,
    rightsReqd,
    dateFrom,
    dateTo,
    imageDataIncluded,
    mediaId,
    predictionModel,
    performanceFrom,
    performanceTo,
    page,
    limit,
    sources,
    nextToken,
    imageData,
    clear,
    signal,
    contentTags,
  }) {
    const url = `/brands/${brandId}/media_search`;
    const data = { image_data: imageData, clear, contentTags };
    const params = {
      image_data: imageDataIncluded,
      brand_id: brandId,
      sort,
      query,
      aspect_ratio: aspectRatio,
      size,
      type,
      color,
      rights_reqd: rightsReqd,
      date_from: dateFrom,
      date_to: dateTo,
      media_id: mediaId,
      prediction_model: predictionModel,
      performance_from: performanceFrom,
      performance_to: performanceTo,
      page,
      limit,
      sources,
      next_token: nextToken,
    };
    return http.put(url, data, { params, signal });
  },

  async getImageData(mediaId) {
    const libraryHttp = createDashHttpClient(env.libraryApiUrl);
    return libraryHttp.get(`/media/${mediaId}/image_data`);
  },

  async fetchMediaProducts({ brandId, mediaId }, config = {}) {
    return http.get(`/brands/${brandId}/media/${mediaId}/media_products`, { ...config });
  },

  async getMediaV2({ brandId, mediaId }, config = {}) {
    return http.get(`/brands/${brandId}/media/v2/${mediaId}`, { ...config });
  },

  async createMedia({ brandId }, data, config = {}) {
    return http.post(`/brands/${brandId}/media`, data, { ...config });
  },

  async createMediaV2({ brandId }, data, config = {}) {
    return http.post(`/brands/${brandId}/media/v2`, data, { ...config });
  },

  async getMediaList(
    { brandId, url, sortFields, sort, filters, include, ids, limit, includeInstagramUsers = false },
    config = {},
  ) {
    const params = {
      include,
    };
    const data = {
      sortFields,
      sort,
      filters,
      ids,
      limit,
      includeInstagramUsers,
    };

    if (url) {
      merge(params, parseQuery(url));
    }

    return http.put(
      `/brands/${brandId}/media/v2`,
      data,
      merge(
        {
          params,
        },
        config,
      ),
    );
  },

  async getBrandMediaListBySource({ brandId, source, sourceIds }, config = {}) {
    return http.get(`/brands/${brandId}/media`, {
      ...config,
      params: { source, sourceIds: sourceIds.join(',') },
    });
  },
  async getTopPostMediaList({ brandId, limit, filters, sortFields, url }, config = {}) {
    const params = { limit };
    const data = { sortFields, filters };

    if (url) {
      merge(params, parseQuery(url));
    }

    const response = await http.put(
      `/brands/${brandId}/media/v2`,
      data,
      merge(
        {
          params,
        },
        config,
      ),
    );
    return response.data.data;
  },

  async getMediaListCSV(
    { brandId, source, filters, sortFields, sort, socketId, limit, fileNamePrefix },
    config = {},
  ) {
    const params = { brandId };
    const data = {
      source,
      filters,
      sortFields,
      sort,
      limit,
      fileNamePrefix,
      socketId,
    };
    return http.put(`/brands/${brandId}/media/v2/csv`, data, {
      ...config,
      params,
    });
  },
  async updateMediaLinks({ mediaId, brandId, data }) {
    return http.patch(`/brands/${brandId}/media/${mediaId}/media_products`, data);
  },
  async getListMediaLinks({ brandId, mediaId }) {
    return http.get(`/brands/${brandId}/media/${mediaId}/media_products`);
  },
  async getSimilarMediaList({ brandId, mediaId, groupId, sources, limit }) {
    const url = `/brands/${brandId}/media_search`;
    const params = { brandId, mediaId, groupId, sources, limit };
    return http.get(url, { params });
  },
  async getMediaSourceList({ brandId, groupId, globalIds }) {
    const url = `/brands/${brandId}/media`;
    const params = { brandId, group_id: groupId, global_ids: globalIds };
    return http.get(url, { params });
  },
  async apiGetCompetitors({ payload }, config = {}) {
    const url = '/competitors/search';
    return http.post(url, payload, config);
  },
  async deleteGoogleAnalyticsAccount(brandId) {
    return http.delete(`/brands/${brandId}/google_analytics/account`);
  },
  async getAutocomplete({ brandId, source, handle }) {
    const updatedSource = source === 'X' ? 'TWITTER' : source;
    const url = `/brands/${brandId}/competitors/autocomplete`;
    const params = { brandId, source: updatedSource, handle };
    return http.get(url, { params });
  },

  async createSegmentCluster({ brandId, targetSource, targetSourceId }, config = {}) {
    return http.post(
      `/brands/${brandId}/segment_clusters`,
      {
        targetSource,
        targetSourceId,
      },
      config,
    );
  },

  async addMediaTag({ brandId, mediaId, name }, config = {}) {
    const params = { brandId, mediaId };
    const data = { name };

    return http.post(`/brands/${brandId}/media/${mediaId}/tags`, data, { ...config, params });
  },

  async addMultipleMediaTags({ brandId, mediaIds, tags }, config = {}) {
    const params = { brandId };
    const data = { media_ids: mediaIds, tags };

    return http.post(`/brands/${brandId}/media_tags`, data, { ...config, params });
  },

  async removeMediaTag({ brandId, mediaId, tagId }, config = {}) {
    const params = { brandId, mediaId, tagId };
    return http.delete(`/brands/${brandId}/media/${mediaId}/tags/${tagId}`, {
      ...config,
      params,
    });
  },

  async updateMediaMeta({ brandId, mediaId, meta }, config = {}) {
    const params = { brandId, mediaId };
    const data = { meta };

    return http.patch(`/brands/${brandId}/media/${mediaId}`, data, {
      ...config,
      params,
    });
  },

  async updateMediaStatus({ brandId, mediaId, status }, config = {}) {
    return http.patch(`/brands/${brandId}/media/${mediaId}`, { status }, config);
  },

  async deleteMediaItem({ brandId, mediaId }, config = {}) {
    const params = { brandId, mediaId };

    return http.delete(`/brands/${brandId}/media/${mediaId}`, { ...config, params });
  },

  async deleteCampaignMediaItem({ brandId, campaignId, mediaIds }, config = {}) {
    return http.delete(`/brands/${brandId}/campaigns/${campaignId}/media?media_ids=${mediaIds}`, {
      ...config,
    });
  },

  async bulkDeleteMedia({ brandIds, mediaIds }, config = {}) {
    return http.delete('/media/bulk', {
      ...config,
      params: { brandIds: brandIds.join(','), mediaIds: mediaIds.join(',') },
    });
  },

  async bulkUpdateMediaStatus({ brandId, mediaIds, status }, config = {}) {
    return http.patch(`/brands/${brandId}/media/bulk`, { media_ids: mediaIds, status }, config);
  },

  async getImageDataByMediaId({ mediaId }, config = {}) {
    const params = { mediaId };

    return http.get(`/media/${mediaId}/image_data`, { ...config, params });
  },

  /**
   * Deprecated can be removed if fetch (imageToBase64) solution will work
   */
  async getImageDataByUrl({ url }, config = {}) {
    const params = { url };

    return http.get('/image_data', { ...config, params });
  },

  async updateProduct({ brandId, productId, data }, config = {}) {
    const url = `/brands/${brandId}/products/${productId}`;
    return http.patch(url, data, { ...config });
  },

  async getProducts({ brandId, url, ...other }, config = {}) {
    let pathName = `/brands/${brandId}/products`;
    let params = merge({}, other);

    if (url) {
      pathName = new URL(url).pathname;
      params = merge(params, parseQuery(url));
    }

    return http.get(
      pathName,
      merge(
        {
          params,
        },
        config,
      ),
    );
  },

  async getProduct({ brandId, productId }, config = {}) {
    return http.get(`/brands/${brandId}/products/${productId}`, { ...config });
  },

  // Returns a product record with url that matches exactly
  // Also searches product override URLs, returning the associated product record if found
  async findProductByUrl({ brandId, url }, config = {}) {
    return http.post(`/brands/${brandId}/products/find_by_url`, { url }, config);
  },

  async addProduct({ brandId }, data, config = {}) {
    return http.post(`/brands/${brandId}/products`, data, { ...config });
  },

  async updateProductArchivedStatusByIds({ brandId, productIds, archived }, config = {}) {
    const data = { product_ids: productIds, archived };
    return http.patch(`/brands/${brandId}/products/archive`, data, { ...config });
  },

  async getProductFeeds({ brandId, sort }, config = {}) {
    const params = { sort };
    return http.get(`/brands/${brandId}/product_feeds`, { ...config, params });
  },

  async addProductFeed({ brandId }, data, config = {}) {
    return http.post(`/brands/${brandId}/product_feeds`, data, { ...config });
  },

  async updateProductFeed({ brandId, feedId }, data, config = {}) {
    return http.post(`/brands/${brandId}/product_feeds/${feedId}`, data, { ...config });
  },

  async archiveProductFeedProducts(
    { brandId, feedId, notificationSocketId, archived },
    config = {},
  ) {
    const data = {
      notification_socket_id: notificationSocketId,
      archived,
    };
    return http.patch(`/brands/${brandId}/product_feeds/${feedId}/products/archive`, data, {
      ...config,
    });
  },

  async deleteProductFeed({ brandId, feedId }, config = {}) {
    return http.delete(`/brands/${brandId}/product_feeds/${feedId}`, { ...config });
  },

  async getUrlMetadata({ url, id, linkNumber, retryFetch = false }, config = {}) {
    const params = { url, id, linkNumber, retry_fetch: retryFetch };
    return http.get(`/url_metadata`, { ...config, params });
  },

  async getProductsAnalytics({ brandId, scale, startDate, endDate }, config = {}) {
    const params = {
      brandId,
      scale,
      start_date: startDate,
      end_date: endDate,
    };
    return http.get(`/brands/${brandId}/products/click_stats`, { ...config, params });
  },

  async getProductMedia({ brandId, productId }, config = {}) {
    return http.get(`/brands/${brandId}/products/${productId}/media`, { ...config });
  },

  async getProductClicks({ brandId, productId, startDate, endDate, scale, source }, config = {}) {
    const params = {
      startDate,
      endDate,
      scale,
      source,
    };
    return http.get(`/brands/${brandId}/products/${productId}/click_stats`, {
      ...config,
      params,
    });
  },

  async setProductMediaHiddenStatus({ brandId, productId, mediaId, hideInWidget }, config = {}) {
    const data = { hide_in_widget: hideInWidget };
    return http.patch(`/brands/${brandId}/products/${productId}/media/${mediaId}`, data, {
      ...config,
    });
  },

  async getMediaBySourceId({ brandId, sourceId, sourceType }, config = {}) {
    const url = `/brands/${brandId}/media/source/${sourceId}`;
    const params = { source_type: sourceType };
    return http.get(url, { ...config, params });
  },

  async getCompetitorTagsByBrand({ brandIds, includeCompetitors = false }, config = {}) {
    const url = '/competitors/tags';
    const data = { brand_ids: brandIds, include_competitors: includeCompetitors };
    return http.put(url, data, config);
  },

  async getUtmSettings({ brandId, channel }, config = {}) {
    const url = `/brands/${brandId}/utm_settings`;
    return utmHttp.get(url, { params: { channel } }, config);
  },

  async getUtmPresetValues({ brandId, channel, trackingKey }, config = {}) {
    const url = `/brands/${brandId}/utm_settings/preset_values`;
    return http.get(url, { params: { channel, trackingKey } }, config);
  },

  async createUtmPresetValue({ brandId, channel, trackingKey, value }, config = {}) {
    const url = `/brands/${brandId}/utm_settings/preset_values`;
    return http.post(url, { channel, trackingKey, value }, config);
  },

  async updateUtmPresetValue({ brandId, id, value }, config = {}) {
    const url = `/brands/${brandId}/utm_settings/preset_values/${id}`;
    return http.put(url, { value }, config);
  },

  async deleteUtmPresetValue({ brandId, id }, config = {}) {
    const url = `/brands/${brandId}/utm_settings/preset_values/${id}`;
    return http.delete(url, config);
  },

  async saveUtmSettings({ brandId, channel, items }, config = {}) {
    const url = `/brands/${brandId}/utm_settings`;
    const data = {
      [channel]: items,
    };
    return utmHttp.put(url, data, config);
  },

  async decodeUtmSettings({ brandId, channel, content, urls }, config = {}) {
    const url = `/brands/${brandId}/utm_settings/decode_utms`;
    return utmHttp.post(url, { channel, content, urls }, config);
  },
  async encodeUtmSettings(
    {
      brandId,
      channel,
      shortenUrl,
      customUtms = {},
      updateShortUrl = false,
      mediaId,
      productId,
      content,
      urls,
    },
    config = {},
  ) {
    return http.post(
      `/brands/${brandId}/utm_settings/encode_utms`,
      {
        channel,
        content,
        mediaId,
        customUtms,
        updateShortUrl,
        productId,
        shortenUrl,
        urls,
      },
      config,
    );
  },

  async createMediaForManyBrands(data, config = {}) {
    return http.post(`/media`, data, { ...config });
  },

  async getAiAlternatives({ brandId, mediaId, parentId, limit, offset }, config = {}) {
    return http.get(`/brands/${brandId}/ai_alternative_media`, {
      ...config,
      params: {
        media_id: mediaId,
        ...(parentId && { parent_id: parentId }),
        ...(limit != null && { limit }),
        ...(offset != null && { offset }),
      },
    });
  },

  async getAiAlternativeById({ brandId, alternativeId }, config = {}) {
    return http.get(`/brands/${brandId}/ai_alternative_media/${alternativeId}`, config);
  },

  async deleteAiAlternativeMedia(brandId, aiAlternativeMediaIds, config = {}) {
    return http.delete(`/ai_alternative_media`, {
      ...config,
      data: { ai_alternative_media_ids: aiAlternativeMediaIds },
    });
  },

  async getAiAlternativeAds({ brandId, sourceAdIds }, config = {}) {
    return http.post(
      `/brands/${brandId}/ai_alternative_ads`,
      {
        source_ad_ids: sourceAdIds,
      },
      config,
    );
  },

  async deleteAiAlternativeAds({ brandId, aiAlternativeAdIds, config = {} }) {
    return http.delete(`/brands/${brandId}/ai_alternative_ads`, {
      ...config,
      data: { ai_alternative_ads_ids: aiAlternativeAdIds },
    });
  },

  async deleteBulkUgcContentRightsFailures({ brandId }, config = {}) {
    return http.delete(`/brands/${brandId}/bulk_ugc_content_rights_failures`, config);
  },

  getContentRightsCsv({ brandId, ...params }, config = {}) {
    return http.get(
      `/brands/${brandId}/content-rights/csv`,
      merge(
        {
          params,
        },
        config,
      ),
    );
  },
};

export default api;
